
import {
  defineComponent,
  PropType,
  ref,
  reactive,
  computed,
  watch,
  toRef,
  onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';

import Dropdown from 'primevue/dropdown';
import Loader from '@/components/layout/Loader.vue';
import { PanelAccordion } from '@/components/flows/operators/shared';
import TemplateSelectorV2 from '@/components/automated-scenarios/fields/TemplateSelectorV2.vue';
import { TemplatePreview, VoucherConfig, MultiVoucherConfig, ReuseCouponSelector } from '@/components/flows/operators/shared';
import SendTestV4Modal from '@/components/modals/SendTestV4Modal.vue';
import { useSendingChannelForm } from '@/composables/flows';
import { showToastError } from '@/helpers';
import { NestApiType, nestCall } from '@/composables/nestApi';
import type { RcsSelectableSender } from '@/types/rcs-onboarding';
import type { SendRcsData } from './index';
import { createDefaultData } from './index';
import SendRcs from './index';

export default defineComponent({
  name: 'ActionSendRcsForm',

  components: {
    Dropdown,
    Loader,
    PanelAccordion,
    TemplateSelectorV2,
    TemplatePreview,
    VoucherConfig,
    MultiVoucherConfig,
    ReuseCouponSelector,
    SendTestV4Modal,
  },

  props: {
    modelValue: {
      type: Object as PropType<SendRcsData>,
      required: true,
      default: () => createDefaultData(),
    },

    formValidation: {
      type: Object,
      default: () => ({}),
    },

    selectedComponent: {
      type: String,
      default: '',
    },

    operatorId: {
      type: String,
      default: undefined,
    },
  },

  emits: ['update:modelValue', 'cancel'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const isLoading = ref(false);
    const panelsKey = ref(0);

    // RCS sender agents available in the box (shared Shopimind + launched ones).
    const senderOptions = ref<RcsSelectableSender[]>([]);
    const sendersLoading = ref(false);

    // Reactive data fields
    const dataFields = reactive<SendRcsData>(createDefaultData({ ...props.modelValue }));

    // Initialize sending channel form composable
    const sendingChannelForm = useSendingChannelForm({
      media: 'richtexting',
      dataFields,
      formValidation: toRef(props, 'formValidation'),
      panelsKey,
      operatorId: props.operatorId,
      onCancel: () => emit('cancel'),
    });

    // Destructure commonly used values from composable
    const {
      campaignId,
      campaignType,
      shopId,
      templateConfig,
      templateSelectorIsOpen,
      formValidationErrors,
      voucherData,
      showVoucherPanel,
      showVoucherConfig,
      showReusePreviousCouponField,
      reusePreviousCouponOptions,
      useMultiVoucherConfig,
      resolvedParentVoucher,
      resolvedExpiringVoucher,
      handleSelectTemplate,
      handleCloseTemplateSelector,
      handleChangeTemplate,
      handleEditTemplate,
      handleSendTestV4,
      handleOpenSendTestModal,
      initialize,
    } = sendingChannelForm;

    // Wrapper to open send test modal with validation
    const openSendTestWithValidation = () => {
      handleOpenSendTestModal(() => SendRcs.validate!(dataFields, { shopId: shopId.value }));
    };

    // Load the selectable RCS sender agents (non-draft) for the dropdown.
    const loadSenderOptions = async () => {
      sendersLoading.value = true;
      try {
        const response = await nestCall(
          'get',
          NestApiType.MESSAGING,
          '/rcs/onboarding/status',
          {},
          '',
          undefined,
          true,
          { shop_id: Number(shopId.value) },
        );
        const data = response?.data?.data || response?.data;
        senderOptions.value = data?.selectable_senders || [];
        // Default to the shared Shopimind sender so the field is never empty.
        if (!dataFields.rcs_sender_id && data?.shared_sender?.sender_id) {
          dataFields.rcs_sender_id = data.shared_sender.sender_id;
        }
      } catch (error) {
        senderOptions.value = [];
      } finally {
        sendersLoading.value = false;
      }
    };

    // Panels configuration
    const panels = computed(() => [
      {
        id: 'template_preview',
        title: 'automatedScenarios.actions.sending_channel.template_preview.title',
      },
      {
        id: 'voucher_config',
        title: 'automatedScenarios.actions.sending_channel.vouchers_config.title',
        hide: !showVoucherPanel.value,
      },
      {
        id: 'message_config',
        title: 'automatedScenarios.actions.sending_channel.message_config.title',
      },
    ]);

    // Emit updates when data changes
    watch(dataFields, () => {
      emit('update:modelValue', createDefaultData({ ...dataFields }));
    }, { deep: true });

    // Initialize on mount
    onMounted(async () => {
      try {
        isLoading.value = true;

        // Load the RCS sender agents for the dropdown.
        await loadSenderOptions();

        // Initialize template config
        await initialize(dataFields.template_display_selector_id);
      } catch (error) {
        showToastError('GENERIC_ERROR');
      } finally {
        isLoading.value = false;
      }
    });

    return {
      t,
      isLoading,
      panelsKey,
      senderOptions,
      sendersLoading,
      formValidationErrors,
      templateConfig,
      templateSelectorIsOpen,
      dataFields,
      voucherData,
      showVoucherPanel,
      showVoucherConfig,
      showReusePreviousCouponField,
      reusePreviousCouponOptions,
      useMultiVoucherConfig,
      resolvedParentVoucher,
      resolvedExpiringVoucher,
      panels,
      campaignId,
      campaignType,
      handleSelectTemplate,
      handleCloseTemplateSelector,
      handleChangeTemplate,
      handleEditTemplate,
      handleSendTestV4,
      handleOpenSendTestModal,
      openSendTestWithValidation,
    };
  },
});
